import React, { useRef, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@material-ui/core';

const CalibrationDialog = ({ open, imageUrl, onClose, onSubmit, areaNames = [], cameraId = null }) => {
    console.log('CalibrationDialog props:', { open, imageUrl, areaNames });
    const retryLimit = 100;
    const maxEditorWidth = 960;
    const maxEditorHeight = 720;
    const minPolygonPoints = 3;
    const [polygons, setPolygons] = useState([]);
    const [currentPoints, setCurrentPoints] = useState([]);
    const [isMarking, setIsMarking] = useState(false);
    const [areaNamesState, setAreaNames] = useState(areaNames);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [retryAttempts, setRetryAttempts] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const [editorSize, setEditorSize] = useState({ width: 640, height: 480 });

    const maxSlots = areaNames.length || null;
    const canMarkMore = !maxSlots || polygons.length < maxSlots;

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const lastCameraIdRef = useRef(null);

    const handleCanvasClick = (e) => {
        if (!isMarking || !canMarkMore) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const newPoints = [...currentPoints, { x, y }];
        setCurrentPoints(newPoints);
    };

    const handleCanvasContextMenu = (e) => {
        if (!isMarking) return;

        e.preventDefault();

        if (currentPoints.length >= minPolygonPoints) {
            handleCompletePolygon();
        }
    };

    const drawPolygons = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        [...polygons, currentPoints].forEach((polygon, index) => {
            if (polygon.length === 0) return;
            const isCompletedPolygon = index < polygons.length;

            ctx.beginPath();
            ctx.moveTo(polygon[0].x, polygon[0].y);
            polygon.slice(1).forEach((pt) => ctx.lineTo(pt.x, pt.y));
            if (isCompletedPolygon && polygon.length >= minPolygonPoints) ctx.closePath();

            ctx.strokeStyle = 'red';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Draw polygon label
            if (isCompletedPolygon && polygon.length >= minPolygonPoints) {
                const centerX = polygon.reduce((sum, pt) => sum + pt.x, 0) / polygon.length;
                const centerY = polygon.reduce((sum, pt) => sum + pt.y, 0) / polygon.length;
                const label = areaNamesState[index] || `Slot ${index + 1}`;
                ctx.fillStyle = 'red';
                ctx.font = '16px Arial';
                ctx.fillText(label, centerX - 5, centerY + 5);
            }

            polygon.forEach(({ x, y }) => {
                ctx.beginPath();
                ctx.arc(x, y, 4, 0, 2 * Math.PI);
                ctx.fillStyle = 'blue';
                ctx.fill();
            });
        });
    };

    const handleImageLoad = () => {
        if (imageRef.current) {
            const { naturalWidth, naturalHeight } = imageRef.current;
            const scale = Math.min(
                maxEditorWidth / naturalWidth,
                maxEditorHeight / naturalHeight,
                1
            );

            setEditorSize({
                width: Math.round(naturalWidth * scale),
                height: Math.round(naturalHeight * scale)
            });
            setImageLoaded(true);
            setIsRetrying(false);
            setRetryAttempts(0);
        }
    };

    const handleImageError = () => {
        console.log('Image failed to load, attempt:', retryAttempts + 1);
        if (retryAttempts < retryLimit) {
            setRetryAttempts(prev => prev + 1);
            setIsRetrying(true);
            // Retry after 2 seconds using the original URL only
            retryTimeoutRef.current = setTimeout(() => {
                if (imageRef.current) {
                    imageRef.current.src = '';
                    imageRef.current.src = imageUrl;
                }
            }, 2000);
        } else {
            setIsRetrying(false);
            console.error('Failed to load image after maximum retries');
        }
    };

    const attemptImageLoad = () => {
        if (!imageUrl) return;

        setImageLoaded(false);
        setIsRetrying(true);
        setRetryAttempts(0);

        // Clear any existing timeout
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }

        // Start loading attempt
        if (imageRef.current) {
            imageRef.current.src = imageUrl;
        }
    };

    const handleRetryFromStart = () => {
        attemptImageLoad();
    };

    useEffect(() => {
        if (open && imageLoaded) {
            drawPolygons();
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, [open, imageLoaded, polygons, currentPoints, areaNamesState, editorSize]);

    useEffect(() => {
        if (open && imageUrl) {
            attemptImageLoad();
        }
        return () => {
            // Cleanup timeout on unmount or dialog close
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, [open, imageUrl]);

    useEffect(() => {
        if (!open) {
            setCurrentPoints([]);
            setIsMarking(false);
            return;
        }

        const isNewCamera = lastCameraIdRef.current !== cameraId;

        if (isNewCamera) {
            setPolygons([]);
            setCurrentPoints([]);
            setAreaNames(areaNames);
            setIsMarking(false);
            setImageLoaded(false);
            setRetryAttempts(0);
            setIsRetrying(false);
            setEditorSize({ width: 640, height: 480 });
            lastCameraIdRef.current = cameraId;
            return;
        }

        setAreaNames(areaNames);
    }, [open, areaNames, cameraId]);

    const handleSave = () => {
        // Convert polygons to the required format
        const formattedPolygons = polygons.map(polygon =>
            polygon.map(point => [Math.round(point.x), Math.round(point.y)])
        );

        onSubmit(formattedPolygons, areaNamesState.slice(0, formattedPolygons.length));
    };

    const handleCompletePolygon = () => {
        if (currentPoints.length < minPolygonPoints) return;

        setPolygons((prev) => [...prev, currentPoints]);
        setCurrentPoints([]);

        if (maxSlots && polygons.length + 1 >= maxSlots) {
            setIsMarking(false);
        }
    };

    const handleUndo = () => {
        if (currentPoints.length > 0) {
            setCurrentPoints((prev) => prev.slice(0, -1));
            return;
        }

        setPolygons((prev) => prev.slice(0, -1));
    };

    const toggleMarking = () => {
        if ((!imageLoaded || !canMarkMore) && !isMarking) return;
        setIsMarking(!isMarking);
        if (isMarking) {
            setCurrentPoints([]);
        }
    };

    const currentSlotLabel = canMarkMore
        ? areaNamesState[polygons.length] || `Slot ${polygons.length + 1}`
        : 'all slots completed';

    const helperText = !maxSlots
        ? 'Click Start Marking Slots to begin slot marking.'
        : isMarking
            ? `Mark slot for ${currentSlotLabel}. Current points: ${currentPoints.length}. Add as many points as needed, then click Finish Polygon or right-click to complete it.`
            : canMarkMore
                ? `Click Start Marking Slots to start slot marking. Completed ${polygons.length}/${maxSlots} slots.`
                : `All slot marking is completed. ${polygons.length}/${maxSlots} slots are ready to save.`;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Calibrate Camera Slots</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box
                        ref={containerRef}
                        style={{
                            position: 'relative',
                            width: `${editorSize.width}px`,
                            height: `${editorSize.height}px`,
                            marginBottom: 16,
                            border: '1px solid #ccc'
                        }}
                    >
                        <img
                            ref={imageRef}
                            src={imageUrl}
                            alt="Camera frame"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: imageLoaded ? 'block' : 'none'
                            }}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                        />
                        <canvas
                            ref={canvasRef}
                            width={editorSize.width}
                            height={editorSize.height}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                pointerEvents: isMarking ? 'auto' : 'none',
                                cursor: isMarking ? 'crosshair' : 'default',
                            }}
                            onClick={handleCanvasClick}
                            onContextMenu={handleCanvasContextMenu}
                        />
                        {!imageLoaded && (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#f5f5f5'
                                }}
                            >
                                <Box textAlign="center">
                                    <Typography>
                                        {isRetrying ? `Loading image... (Attempt ${retryAttempts + 1}/${retryLimit})` : 'Preparing image...'}
                                    </Typography>
                                    {retryAttempts >= retryLimit && (
                                        <>
                                            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                                                Failed to load image after multiple attempts
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={handleRetryFromStart}
                                                style={{ marginTop: 12 }}
                                            >
                                                Try Again
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Box
                        style={{
                            width: `${editorSize.width}px`,
                            maxWidth: '100%',
                            marginTop: 8,
                            alignSelf: 'center'
                        }}
                    >
                        <Typography variant="body2" color="textSecondary" style={{ textAlign: 'left' }}>
                            {helperText}
                        </Typography>
                    </Box>

                    <Box display="flex" flexWrap="wrap" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
                        <Box sx={{ m: 1 }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleUndo}
                                disabled={polygons.length === 0 && currentPoints.length === 0}
                            >
                                {currentPoints.length > 0 ? 'Undo Last Point' : 'Remove Last Slot'}
                            </Button>
                        </Box>

                        <Box sx={{ m: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCompletePolygon}
                                disabled={!isMarking || currentPoints.length < minPolygonPoints}
                            >
                                Finish Polygon
                            </Button>
                        </Box>

                        <Box sx={{ m: 1 }}>
                            <Button
                                variant="contained"
                                color={isMarking ? 'secondary' : 'primary'}
                                onClick={toggleMarking}
                                disabled={(!imageLoaded || !canMarkMore) && !isMarking}
                            >
                                {isMarking ? 'Stop Marking Slots' : 'Start Marking Slots'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions style={{ padding: 15 }}>
                <Button onClick={onClose} color="default" variant="outlined">
                    Close
                </Button>
                {polygons.length > 0 && (
                    <Button onClick={handleSave} color="primary" variant="contained" disabled={isMarking || currentPoints.length > 0}>
                        Save Calibration
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CalibrationDialog;
